// What the Work-board offers on a selection of email THREADS (busy-my-web#274, AI Brain spec §6,
// Appendix §2, §13).
//
// Until now a ticked email did almost nothing. The board's "What would you like to do?" was written
// for ticked sender GROUPS — "Archive selected groups", "Delete selected groups" (which had no
// endpoint at all and was drawn greyed), "Create ongoing" — and ticking individual threads got you
// those same three sentences, two of which were about the wrong noun and one of which could not run
// at all, because creating an ongoing from picks follows SENDERS and a thread pick carries none.
//
// So threads get their own row. The verbs are the spec's, in the spec's order, and every one of them
// now has something behind it:
//
//   Create Ongoing (suggested) · Add to existing Ongoing · Create Email Group · Summarise threads
//   · Create Task · Archive · Mark read · Mark unread · Delete · Export
//
// Pure by design, like the rest of lib/emailSession.js: no store, no router, nothing mounted. The
// views render what these return, and "which verbs does a two-thread selection get" is answerable
// without a browser.

// Nothing is imported from lib/emailSession.js on purpose, even though the thread filter below is a
// narrower `sessionPicks`. emailSession imports THIS module (its suggestion list branches to the
// thread row), and importing back would make the two files a cycle — which ESM tolerates until the
// day one of them is loaded first and reads an undefined binding at module scope.

/** Verbatim labels. One module so a wording cannot drift between the row, the CTA and the receipt. */
export const THREAD_TEXT = {
  createOngoing: 'Create ongoing',
  addToOngoing: 'Add to ongoing',
  createGroup: 'Create Email Group',
  summarise: 'Summarise threads',
  createTask: 'Create Task',
  archive: 'Archive',
  markRead: 'Mark read',
  markUnread: 'Mark unread',
  delete: 'Delete',
  export: 'Export',
  // The MAILBOX listing's own four (frame "Email page listing-select all", 40009853:294742 —
  // Pouria, 2026-09-21): the nouns are conversations, and the two marks say "all".
  listingArchive: 'Archive conversations',
  listingDelete: 'Delete conversations',
  listingImportant: 'Mark as important all',
  listingRead: 'Mark as read all',
  markImportant: 'Mark as important',
  // The chooser that opens on "Add to existing Ongoing".
  choosePanelTitle: 'Add to an ongoing',
  choosePlaceholder: 'Search your ongoings',
  chooseEmpty: 'No ongoing matches that.',
  chooseNone: 'You have no ongoings yet.',
  // The naming step that opens on "Create Email Group".
  groupPanelTitle: 'Create an email group',
  groupNameLabel: 'Group name:',
  groupNamePlaceholder: 'Name this group',
  // What the summary callout says while it runs and when it could not read the mail.
  summarising: 'Reading the selected threads...',
  summaryFallback: 'Busy.me could not read these threads just now — this is what it knows about them.',
  saveAsNote: 'Save as note',
  // ── The CTAs the newer choosers put in the action bar ──
  // The matched-groups chooser (busy-my-web#422, frames 40010687:355542 → 40010788:358603) and the
  // attachment selection (busy-my-web#423, 40011000:380706 → 40011008:402606). They live here with
  // every other action-bar word so a wording cannot drift between the radio and the button.
  connect: 'Connect',
  dismiss: 'Dismiss',
  add: 'Add',
  downloadAll: 'Download all',
  saveToFiles: 'Save to Files',
}

/**
 * The threads a selection stands for — the timeline ids the endpoints take.
 *
 * A thread pick's `emailId` IS a timeline id: the rows it is built from come from the timeline
 * listing, and a thread owns exactly one timeline for life (acc-account-sync-srv#11). That identity
 * is the whole reason this feature needs no translation step — the board already holds the id the
 * graph, the mail service and the object resolver all speak.
 */
export function pickedTimelineIds(items) {
  const ids = []
  const seen = new Set()
  for (const pick of items || []) {
    if (!pick || pick.kind !== 'email-pick') continue
    const id = pick.emailId || pick.objectRef?.id
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

/** True when anything ticked is a thread — which is what puts the board on the thread row. */
export function hasThreadPicks(items) {
  return pickedTimelineIds(items).length > 0
}

/**
 * "3 threads" / "1 thread" — the phrase every line of this journey quotes, so a selection is never
 * "3 threads" in the row and "3 emails" in the receipt.
 */
export function threadsLabel(n) {
  const count = Number(n) || 0
  return `${count} thread${count === 1 ? '' : 's'}`
}

/**
 * The action row for a thread selection, in the spec's order.
 *
 * `ready` is kept honest, exactly as it always has been: an option with no endpoint is still SHOWN
 * (the design writes it) and marked, rather than being quietly wired to nothing. Since this issue
 * every verb here is ready — including Delete, which the board has drawn greyed since the email
 * session shipped — with one exception noted inline.
 *
 * Only ONE of Mark read / Mark unread is offered at a time, decided by what is selected: a
 * selection that is entirely read has nothing to mark read, and offering both would make the row two
 * lines longer for no decision the user can meaningfully make. `unread` is the count of unread
 * messages across the selection, which the caller reads off the same rows it drew.
 */
export function threadActions({ items = [], unread = null, listing = false } = {}) {
  const ids = pickedTimelineIds(items)
  if (!ids.length) return []

  // ON THE MAILBOX LISTING (/emails/inbox and its folders) the frame asks a different four
  // (40009853:294742, Pouria 2026-09-21): archive / delete / mark important / mark read — the verbs
  // of a mailbox, not of an Ongoing. No "Recommended" mark on any of them: the frame draws none.
  if (listing) {
    return [
      { id: 'threads-archive', text: THREAD_TEXT.listingArchive, recommended: false, ready: true },
      { id: 'threads-delete', text: THREAD_TEXT.listingDelete, recommended: false, ready: true },
      { id: 'threads-mark-important', text: THREAD_TEXT.listingImportant, recommended: false, ready: true },
      { id: 'threads-mark-read', text: THREAD_TEXT.listingRead, recommended: false, ready: true },
    ]
  }

  const label = threadsLabel(ids.length)
  // Unknown (null) is treated as "there is something unread": marking an already-read thread read
  // is a harmless no-op, whereas hiding the verb on a guess would hide a working action.
  const anyUnread = unread == null || Number(unread) > 0

  // FOUR verbs, in the frame's order and words (frame 40008354:240503, Pouria 2026-09-16: "Archive
  // (Recommended) / Delete / Create ongoing / Add to ongoing"). The eight-verb list the 2026-09-02
  // clarification drew (40009470-209128 — summarise, create task, mark read/unread, export) is
  // superseded by this frame; the handlers for those verbs stay in place, unoffered, exactly as
  // "Create Email Group" already did. `label` and `anyUnread` are computed for the store's own
  // bulk verbs (below) and are not printed here any more.
  void label; void anyUnread
  return [
    { id: 'threads-archive', text: THREAD_TEXT.archive, recommended: true, ready: true },
    { id: 'threads-delete', text: THREAD_TEXT.delete, recommended: false, ready: true },
    { id: 'threads-create-ongoing', text: THREAD_TEXT.createOngoing, recommended: false, ready: true },
    { id: 'threads-add-ongoing', text: THREAD_TEXT.addToOngoing, recommended: false, ready: true },
  ]
}

/** The armed options that commit a bulk verb, and which verb each one is. */
const BULK_VERBS = {
  'threads-archive': 'archive',
  'threads-delete': 'delete',
  'threads-mark-read': 'markRead',
  'threads-mark-unread': 'markUnread',
}

/** The bulk verb an armed option commits, or null when the option is not a bulk one. */
export function bulkVerb(armed) {
  return BULK_VERBS[armed] || null
}

/** The body `POST v1/email/threads/bulk` takes. */
export function bulkRequest(timelineIds, action) {
  return { timelineIds: (timelineIds || []).filter(Boolean), action }
}

/**
 * The CTA's label for an armed thread option — the word the primary button wears in the box's
 * action bar. Null for options that open a PANEL rather than commit something (the ongoing chooser,
 * the group naming step): those bring their own button, and a second one in the action bar saying
 * the same thing would be two ways to press one control.
 */
export function threadCta(armed, { count = 0 } = {}) {
  const label = threadsLabel(count)
  switch (armed) {
    case 'threads-create-ongoing': return THREAD_TEXT.createOngoing
    case 'threads-summarise': return THREAD_TEXT.summarise
    case 'threads-create-task': return THREAD_TEXT.createTask
    case 'threads-archive': return `${THREAD_TEXT.archive} ${label}`
    case 'threads-mark-read': return THREAD_TEXT.markRead
    case 'threads-mark-unread': return THREAD_TEXT.markUnread
    case 'threads-mark-important': return THREAD_TEXT.markImportant
    case 'threads-delete': return `${THREAD_TEXT.delete} ${label}`
    case 'threads-export': return THREAD_TEXT.export
    // busy-my-web#422 — accepting or refusing the offers on the ticked groups. The frame's CTA is
    // the verb of the option, exactly as it is for every thread verb above.
    case 'matches-connect': return THREAD_TEXT.connect
    case 'matches-dismiss': return THREAD_TEXT.dismiss
    // busy-my-web#423 — what a selection of ATTACHMENTS commits. "Add" is the frames' word on both
    // the chooser (40011004:395777) and the matches pane it opens.
    case 'attachments-download': return THREAD_TEXT.downloadAll
    case 'attachments-save-files': return THREAD_TEXT.add
    case 'attachments-create-ongoing': return THREAD_TEXT.createOngoing
    default: return null
  }
}

/** Which armed options open a panel of their own instead of committing straight away. The
 * create-group option is no longer OFFERED (see `threadActions`) but still opens its panel if armed,
 * so an old armed state cannot strand a session. */
export function opensPanel(armed) {
  return PANEL_OPTIONS.has(armed)
}

/**
 * Which options are a DOOR rather than a verb.
 *
 * Since busy-my-web#421 most of them open the page-level "Ongoing Workspace" picker rather than the
 * inline chooser — the group listing's answer, the matched-groups chooser's "Connect to another
 * Ongoing" (#422), the open group's "Add to Ongoing" (#422) and a selection of attachments' (#423).
 * WHICH panel each one opens is `commitThread`'s to say; this only says that arming them is the
 * whole gesture, because there is no second step for the action bar to wait for.
 */
const PANEL_OPTIONS = new Set([
  'threads-add-ongoing',
  'threads-create-group',
  'matches-connect-another',
  'group-add-ongoing',
  'attachments-add-ongoing',
])

/**
 * The receipt a finished bulk verb leaves on the rail, in the shape the archive receipt already
 * uses — so the timeline has one kind of entry for "something happened to these emails" rather than
 * one per verb.
 *
 * The count comes from the SERVER's answer where it gave one (`affected`), falling back to what was
 * asked for. A receipt must never claim more than was actually done: a selection that was already
 * half archived did not archive nine threads, and the user can count.
 */
export function bulkReceipt({ action, timelineIds = [], result = null, at = new Date() } = {}) {
  const asked = (timelineIds || []).length
  const affected = Number.isFinite(Number(result?.threads)) && result?.threads != null
    ? Number(result.threads)
    : asked
  const when = at instanceof Date ? at : new Date(at)
  return {
    id: `email-threads:${action}:${when.getTime()}`,
    icon: RECEIPT_ICON[action] || 'sms',
    title: `${threadsLabel(affected)} ${RECEIPT_VERB[action] || action}`,
    description: 'Email Workspace',
    date: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(when),
    time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: true }).format(when)
      .toLowerCase().replace(/\s?(am|pm)/, ' $1'),
    detail: result?.messages ? `${result.messages} message${result.messages === 1 ? '' : 's'}` : '',
    rows: [],
  }
}

const RECEIPT_VERB = {
  archive: 'archived',
  delete: 'deleted',
  markRead: 'marked read',
  markUnread: 'marked unread',
}

const RECEIPT_ICON = {
  archive: 'archive',
  delete: 'trash',
  markRead: 'sms',
  markUnread: 'sms',
}

// ── Export ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The file an export is saved as: "busy-email-threads-2026-08-29.json".
 *
 * Dated rather than named after the selection, because a selection has no name — and a file called
 * "3 threads.json" in a downloads folder next to yesterday's "3 threads.json" is two files nobody
 * can tell apart.
 */
export function exportFilename(at = new Date()) {
  const when = at instanceof Date ? at : new Date(at)
  const day = Number.isNaN(when.getTime()) ? new Date() : when
  const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
  return `busy-email-threads-${iso}.json`
}

/**
 * The document that is written to disk: the server's bundle, plus a header saying what it is and
 * when it was taken.
 *
 * The header matters. A bare array of threads in a downloads folder six months from now is a JSON
 * file of unknown origin; `exportedAt` and `source` make it self-describing, which is the difference
 * between an export and a dump.
 */
export function exportDocument(bundle, at = new Date()) {
  const when = at instanceof Date ? at : new Date(at)
  return {
    source: 'Busy.me',
    exportedAt: (Number.isNaN(when.getTime()) ? new Date() : when).toISOString(),
    threadCount: (bundle?.threads || []).length,
    threads: bundle?.threads || [],
  }
}

// ── Add to an existing Ongoing ──────────────────────────────────────────────────────────────────

/**
 * The chooser's list: the user's ongoings, filtered as they type, best match first.
 *
 * Matching is on the title only, case- and accent-insensitively, and a PREFIX beats a contained
 * match — typing "he" should surface "Health issues" above "The kitchen", which alphabetical order
 * or raw `includes` would not. Archived ongoings are left out: filing new mail into something the
 * user has closed is almost never what they meant, and they can always re-open it first.
 */
export function ongoingMatches(ongoings, query, limit = 8) {
  const term = normalise(query)
  const live = (ongoings || []).filter(o => o && o.id && !o.isArchived)
  const scored = []
  for (const o of live) {
    const title = normalise(o.title || o.name || '')
    if (term && !title.includes(term)) continue
    scored.push({
      id: o.id,
      title: String(o.title || o.name || '').trim() || 'Untitled',
      // 0 = starts with what was typed, 1 = merely contains it. Stable within each band, so with an
      // empty box the list is simply the ongoings in the order the store holds them (newest first).
      rank: term && title.startsWith(term) ? 0 : 1,
    })
  }
  return scored
    .map((row, i) => ({ ...row, at: i }))
    .sort((a, b) => a.rank - b.rank || a.at - b.at)
    .slice(0, Math.max(0, limit))
    .map(({ id, title }) => ({ id, title }))
}

/** Lower-cased and accent-stripped, so "Café" matches "cafe". */
function normalise(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

/**
 * What the confirmation says after threads joined a story: "3 threads added to Health issues", and
 * the honest note when part of the selection was already there.
 *
 * The server counts, not the client: a selection overlapping what the story already holds is the
 * ordinary case, and quoting what was ASKED for would claim additions that did not happen.
 */
export function addedToOngoingNotice(result, ongoingTitle) {
  const linked = Number(result?.linked) || 0
  const already = Number(result?.alreadyLinked) || 0
  const name = String(ongoingTitle || '').trim()
  if (!linked && already) {
    return { kind: 'added', text: `Already in ${name || 'that ongoing'}` }
  }
  const head = `${threadsLabel(linked)} added${name ? ` to ${name}` : ''}`
  return { kind: 'added', text: already ? `${head} (${already} already there)` : head }
}
